import {
  Button,
  Center,
  Flex,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  Box,
  Divider,
} from '@chakra-ui/react';
import {
  ActionIconButton,
  AppClient,
  useToast,
  DeleteOutline,
  AddCircleOutline,
} from '@aslanpay/portal-common-lib';
import { format, parseISO } from 'date-fns';

import TextWithClipboard from 'app/components/UI/TextWithClipboard';
import useDeleteAppClient from 'app/hooks/useDeleteAppClient';
import useFetchCognitoAppClients from 'app/hooks/useFetchCognitoAppClients';

import { AddAppClientModal } from '../AddAppClientModal';
import { DefaultAuthMethodSection } from '../DefaultAuthMethodSection';
import { IdentityProvidersSection } from '../IdentityProvidersSection';
import { PartnerIntegrationsSection } from '../PartnerIntegrationsSection';
import { ScimInstructionsModal } from '../ScimInstructionsModal';

import { AppClientWithCreatedBy, AuthIntegrationTabProps } from './AuthIntegrationTab.types';

function AuthIntegrationTab({ companyId = '' }: AuthIntegrationTabProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isScimInstructionsOpen,
    onOpen: onOpenScimInstructions,
    onClose: onCloseScimInstructions,
  } = useDisclosure();
  const toast = useToast();
  const deleteAppClientMutation = useDeleteAppClient();

  const { data: appClients, isLoading } = useFetchCognitoAppClients(companyId);

  const handleAddSuccess = () => {
    toast({
      position: 'top-right',
      status: 'success',
      title: 'App client added',
      description: 'New Cognito app client has been successfully added!',
    });
  };

  const handleDeleteAppClient = async (clientId: string) => {
    try {
      if (!companyId) return;
      await deleteAppClientMutation.mutateAsync({ companyId, clientId });
      toast({
        position: 'top-right',
        status: 'success',
        title: 'App client deleted',
        description: 'The app client has been successfully deleted!',
      });
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the app client could not be deleted. Please try again.',
      });
    }
  };

  return (
    <Flex flexDir="column" gap="20px" w="100%">
      <DefaultAuthMethodSection companyId={companyId} />

      <Box border="1px solid" borderColor="brand.lightGray1" borderRadius="8px" p="20px">
        <Flex flexDir="column" gap="20px" w="100%">
          <Text as="h5" textStyle="h5SemiBold">
            Integrations
          </Text>

          <IdentityProvidersSection companyId={companyId} />

          <Divider />

          <PartnerIntegrationsSection companyId={companyId} />

          <Divider />

          <Flex flexDir="column" gap="6px">
            <Text as="h6" textStyle="h6SemiBold">
              SCIM provisioning
            </Text>
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              App clients are the secure credentials your identity provider (for example, Microsoft
              Entra) uses to connect to Aslan and automatically keep your employee list up to date —
              adding, updating, and removing people for you. Add one to turn on automatic user
              provisioning so you don&apos;t have to manage employees by hand.{' '}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                color="brand.purple"
                textDecoration="underline"
                cursor="pointer"
                onClick={onOpenScimInstructions}
                data-testid="open-scim-instructions"
              >
                Learn how to set up the integration.
              </Link>
            </Text>
          </Flex>

          {isLoading && (
            <Center py="40px">
              <Spinner color="brand.purple" size="md" />
            </Center>
          )}
          {!isLoading && appClients && appClients.length > 0 && (
            <Flex
              overflowX="auto"
              border="1px solid"
              borderColor="brand.lightGray1"
              borderRadius="8px"
            >
              <Table variant="simple" size="sm">
                <Thead bgColor="brand.lightGray2">
                  <Tr>
                    <Th>ID</Th>
                    <Th>Client Name</Th>
                    <Th>Client ID</Th>
                    <Th>Created At</Th>
                    <Th>Created By</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {appClients?.map((client: AppClient) => (
                    <Tr key={client.id}>
                      <Td fontSize="sm">
                        <TextWithClipboard value={client.id} />
                      </Td>
                      <Td fontSize="sm">{client.clientName}</Td>
                      <Td fontSize="sm" fontFamily="Inconsolata, monospace">
                        <TextWithClipboard value={client.clientId} />
                      </Td>
                      <Td fontSize="sm">
                        {client.createdAt
                          ? format(parseISO(client.createdAt), 'dd/MM/yyyy HH:mm')
                          : '-'}
                      </Td>
                      <Td fontSize="sm">
                        {(client as AppClientWithCreatedBy).createdByName || '-'}
                      </Td>
                      <Td textAlign="center">
                        <ActionIconButton
                          icon={<DeleteOutline boxSize="18px" />}
                          aria-label="Delete app client"
                          colorScheme="red"
                          isLoading={deleteAppClientMutation.isPending}
                          onClick={() => handleDeleteAppClient(client.id)}
                        />
                      </Td>
                    </Tr>
                  ))}
                  <Tr
                    cursor="pointer"
                    onClick={onOpen}
                    _hover={{ bg: 'brand.lightGray2' }}
                    data-testid="add-app-client-row"
                  >
                    <Td colSpan={6}>
                      <Flex align="center" justify="center" gap="8px" color="brand.purple">
                        <AddCircleOutline boxSize="18px" />
                        <Text textStyle="smallMedium">Add app client</Text>
                      </Flex>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Flex>
          )}
          {!isLoading && (!appClients || appClients.length === 0) && (
            <Flex
              w="100%"
              py="60px"
              px="20px"
              bgColor="brand.lightGray2"
              borderRadius="8px"
              justify="center"
              align="center"
              flexDir="column"
              gap="16px"
            >
              <Text textStyle="body" color="brand.darkGray">
                No app clients configured yet
              </Text>
              <Button leftIcon={<AddCircleOutline boxSize="20px" />} size="sm" onClick={onOpen}>
                Add your first app client
              </Button>
            </Flex>
          )}
        </Flex>
      </Box>

      <AddAppClientModal
        companyId={companyId}
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handleAddSuccess}
      />

      <ScimInstructionsModal isOpen={isScimInstructionsOpen} onClose={onCloseScimInstructions} />
    </Flex>
  );
}

export { AuthIntegrationTab };

export default AuthIntegrationTab;
